import React from "react";
import {AxiosResponse} from "axios";
import {HttpErrorModel} from "../../../common/models/httpError";
import {RouteComponentProps, withRouter} from "react-router-dom";
import {observer} from "mobx-react";
import Form from "../../../common/components/shared/form/form";
import {routingAddresses} from "../../../helpers/routing";
import i18n from "../../../i18n/i18n";
import PriceFormSection from "./priceFormSection/priceFormSection";
import {storesContext} from "../../../common/stores/stores";
import {Api} from "../../../helpers/api";
import {HttpError} from "../../../common/components/shared/httpError/httpError";
import {IPriceStore} from "../../stores/priceStore";
import {Utils} from "../../../helpers/utils";
import {NewPriceListModel, ParkingRate} from "../../models/priceList";
import {IPriceInStore} from "../../stores/priceInStore";
import {DateUtils} from "../../../helpers/dateUtils";
import {IParkingRateInStore} from "../../stores/parkingRateInStore";
import {PriceListService} from "../../services/priceListService";
import "./priceForm.scss";
import Button from "../../../common/components/shared/button/button";
import {Popup} from "../../../common/components/shared/popup/popup";
import {ReactComponent as RemoveIcon} from "../../../statics/icons/icon_remove.svg";

interface PriceFormState {
    selectedElement: number | undefined;
    isSubmitButtonBlocked: boolean;
    response: AxiosResponse<HttpErrorModel[] | any> | undefined;
    displayPopup: boolean;
    removeItemResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
}

interface PriceFormProps extends RouteComponentProps {
    isUpdate: boolean;
    priceId?: string;
}

class PriceForm extends React.Component <PriceFormProps, PriceFormState> {
    static contextType = storesContext;

    constructor(props: any) {
        super(props);

        this.state = {
            selectedElement: undefined,
            isSubmitButtonBlocked: false,
            response: undefined,
            displayPopup: false,
            removeItemResponse: undefined
        };
    }

    componentWillUnmount(): void {
        this.context.priceStore.clearStore();
    }

    render(): JSX.Element {
        return (
            <Form
                class="price-form"
                renderContent={this.renderContent()}
                renderError={this.renderErrors()}
                submit={this.submit.bind(this)}
                backAddress={routingAddresses.priceList.list}
                isSubmitButtonBlocked={this.state.isSubmitButtonBlocked}
                saveButtonText={this.props.isUpdate ? i18n.t("save") : i18n.t("priceList.addPrice.saveButtonText")}
            />
        );
    }

    private renderErrors() {
        if (!Api.isSuccess(this.state.response)) {
            return (
                <HttpError
                    response={this.state.response}
                    cause={this.getErrorMessage()}
                />
            );
        }
    }

    private renderContent() {
        const priceStore = this.context.priceStore as IPriceStore;

        return (
            <>
                {priceStore.priceList.map((element, priceIndex) =>
                    <PriceFormSection
                        key={`price-form-section-${priceIndex}`}
                        priceId={this.props.priceId}
                        priceIndex={priceIndex}
                        isUpdate={this.props.isUpdate}
                        onRemovePrice={this.onRemovePriceList.bind(this, priceIndex)}
                        isVisibleRemoveButton={priceStore.priceList.length > 1}
                    />
                )}
                {this.renderAddActionContent()}
            </>
        );
    }

    protected renderAddActionContent() {
        return <>
            {this.renderRemovePopup(this.confirmRemove.bind(this))}
            {!this.props.isUpdate && <Button
                text={i18n.t("priceList.actions.addNextPriceList")}
                type="primary"
                variant="text"
                className="add-next-price-btn"
                testId="add-next-price"
                onClick={() => this.addNextPriceList()}
            />}
        </>;
    }

    protected renderRemovePopup(confirmRemove: any) {
        const value = i18n.t("priceList.removePriceList.price");
        const header = `${i18n.t("priceList.removePriceList.header", {value: value})}`;
        const secondHeader = `${i18n.t("priceList.removePriceList.secondHeader", {value: value})}`;
        const content = <div>
            <p>{i18n.t("priceList.removePriceList.line1", {value: value})}</p>
            <p>{i18n.t("priceList.removePriceList.line2")}</p>
        </div>;

        return (
            <Popup
                open={this.state.displayPopup}
                className="remove-popup popup-with-secondary-button-color"
                header={header}
                secondHeader={secondHeader}
                icon={<RemoveIcon/>}
                content={content}
                actionText={i18n.t("priceList.removePriceList.actionText")}
                action={confirmRemove}
                cancel={() => this.setState({displayPopup: false, removeItemResponse: undefined})}
                testId="remove-popup"
                httpError={
                    <>
                        {!Api.isSuccess(this.state.removeItemResponse) && (
                            <HttpError
                                response={this.state.removeItemResponse}
                                cause={i18n.t("priceList.removePriceList.errorMessagePart1")}
                            />
                        )}
                    </>
                }
            />
        );
    }

    private async confirmRemove() {
        if (!this.props.isUpdate) {
            const priceStore = this.context.priceStore as IPriceStore;

            if (this.state.selectedElement !== undefined) {
                priceStore.removePriceList(this.state.selectedElement);

                this.setState({selectedElement: undefined, displayPopup: false});
            }
        }
    }

    private addNextPriceList() {
        const priceStore = this.context.priceStore as IPriceStore;
        priceStore.createPriceList();
    }

    private getErrorMessage() {
        return this.props.isUpdate ? i18n.t("priceList.errors.editErrorSuffix") : i18n.t("priceList.errors.addErrorSuffix");
    }

    private onRemovePriceList(priceId: number) {
        if (!this.props.isUpdate) {
            this.setState({selectedElement: priceId});
        }

        this.setState({displayPopup: true});
    }

    private get isValid(): boolean {
        const priceStore = this.context.priceStore as IPriceStore;
        const priceList = priceStore.priceList;
        let isValid = true;

        priceList.forEach((price) => {
            if (!price.priceIsValid()) {
                isValid = false;
            }
        });

        return isValid;
    }

    private async submit(): Promise<void> {
        const priceStore = this.context.priceStore as IPriceStore;

        if (this.isValid) {
            this.setState({isSubmitButtonBlocked: true, response: undefined});

            if (this.props.isUpdate && this.props.priceId) {
                const priceList = this.preparePrice(priceStore.priceList[0]);

                if (priceList) {
                    this.setState({
                        response: await PriceListService.modifyPrice({parkingPriceLists: [priceList]}, this.props.priceId)
                    });
                }
            } else {
                const priceList: NewPriceListModel[] = [];

                priceStore.priceList.forEach((price: IPriceInStore) => {
                    const simplePrice = this.preparePrice(price);

                    if (simplePrice) {
                        priceList.push(simplePrice);
                    }
                });

                this.setState({response: await PriceListService.addPrice({parkingPriceLists: priceList})});
            }

            this.setState({isSubmitButtonBlocked: false});

            if (Api.isSuccess(this.state.response)) {
                this.props.history.push(routingAddresses.priceList.list);
            }
        }
        Utils.scrollToFirstErrorMessage();
    }

    private preparePrice(price: IPriceInStore): NewPriceListModel | null {
        return {
            priceListType: price.priceListType.value,
            startDate: DateUtils.getDateWithoutTimeToUtc(price.startDate.value),
            parkingRates: this.prepareParkingRates(price.parkingRates, price.parkingFeeForOther.value)
        };
    }

    private prepareParkingRates(parkingRates: IParkingRateInStore[], parkingFeeForOther: string | number | null): ParkingRate[] {
        const preparedParkingRates: ParkingRate[] = [];

        for (const parkingRateElement of parkingRates) {
            preparedParkingRates.push({
                parkingTime: parkingRateElement.parkingTime.value,
                parkingFee: Utils.changeCommaToDot(parkingRateElement.parkingFee.value)
            });
        }

        const otherRates = {
            parkingTime: null,
            parkingFee: Utils.changeCommaToDot(parkingFeeForOther)
        } as ParkingRate;

        return preparedParkingRates.length !== 0 ? preparedParkingRates.concat(otherRates) : [otherRates];
    }
}

export default withRouter(observer(PriceForm));